// Sign Account Op — desktop/responsive layout for the mobile-web (react-native-web)
// build. Webpack resolves this `.web.tsx` over `SignAccountOpScreen.tsx`; native
// iOS/Android keep the `.tsx` (which uses `useController`, and would crash on
// mobile-web via getSnapshot — see project memory).
//
// The screen content (account/dApp card, Overview, Pay-fee, action buttons) is
// the real sign flow — it reuses the @web sign sub-components and the @web
// context hooks. We only re-chrome it: instead of the standalone notification
// window (TabLayoutContainer), the content lives inside the Home v2 AppShell so
// it gets the sidebar on desktop and the bottom tab bar on mobile, matching the
// rest of the wallet. All colors come from the theme tokens (dark/light aware).
//
// Responsive tiers (shared with Home v2 via useHomeBreakpoints + AppShell):
//   >= 1200px : sidebar + centered single column; the column owns scrolling and
//               gates the Sign button until the user reaches the bottom.
//   768–1199  : sidebar + AppShell's single scroll (whole page scrolls).
//   < 768px   : bottom tab bar + AppShell's single scroll.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Image, NativeScrollEvent, Pressable, ScrollView, StyleSheet, View } from 'react-native'

import { SigningStatus } from '@ambire-common/controllers/signAccountOp/signAccountOp'
import { Key } from '@ambire-common/interfaces/keystore'
import { CallsUserRequest } from '@ambire-common/interfaces/userRequest'
import { isSmartAccount } from '@ambire-common/libs/account/account'
import { getErrorCodeStringFromReason } from '@ambire-common/libs/errorDecoder/helpers'
import { getBenzinUrlParams } from '@ambire-common/utils/benzin'
import CopyIcon from '@common/assets/svg/CopyIcon'
import LeftArrowIcon from '@common/assets/svg/LeftArrowIcon'
import AccountAddress from '@common/components/AccountAddress'
import Alert from '@common/components/Alert'
import AlertVertical from '@common/components/AlertVertical'
import Avatar from '@common/components/Avatar'
import ButtonWithLoader from '@common/components/ButtonWithLoader/ButtonWithLoader'
import HoldToProceedButton from '@common/components/HoldToProceedButton'
import NetworkBadge from '@common/components/NetworkBadge'
import NoKeysToSignAlert from '@common/components/NoKeysToSignAlert'
import Text from '@common/components/Text'
import { useReceiveSheet } from '@common/contexts/receiveSheetContext'
import useNavigation from '@common/hooks/useNavigation'
import useReverseLookup from '@common/hooks/useReverseLookup'
import useSign from '@common/hooks/useSign'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import { getSigningProgressText } from '@common/modules/sign-account-op/utils/signingProgress'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { setStringAsync } from '@common/utils/clipboard'
import AppShell from '@mobile/modules/dashboard/screens/home/components/AppShell'
import { NavItem } from '@mobile/modules/dashboard/screens/home/navConfig'
import useHomeBreakpoints from '@mobile/modules/dashboard/screens/home/useHomeBreakpoints'
import { FEATURES } from '@web/constants/features'
import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useMainControllerState from '@web/hooks/useMainControllerState'
import useRequestsControllerState from '@web/hooks/useRequestsControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import useSignAccountOpControllerState from '@web/hooks/useSignAccountOpControllerState'
import Estimation from '@web/modules/sign-account-op/components/Estimation'
import Modals from '@web/modules/sign-account-op/components/Modals/Modals'
import PendingTransactions from '@web/modules/sign-account-op/components/PendingTransactions'
import SafetyChecksOverlay from '@web/modules/sign-account-op/components/SafetyChecksOverlay'
import SectionHeading from '@web/modules/sign-account-op/components/SectionHeading'
import Simulation from '@web/modules/sign-account-op/components/Simulation'
import SigningKeySelect from '@web/modules/sign-message/components/SignKeySelect'

const CONTENT_MAX_WIDTH = 760

const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }: NativeScrollEvent) => {
  const paddingToBottom = 20
  return layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom
}

// `asOverlay` is set by SignAccountOpOverlay (mobile-web): the screen renders on
// top of the swap screen (which owns the live CCTP hook) instead of behind a
// route. In that mode every nav-away action (back / reject-when-last / batch)
// must NOT `navigate` — changing the route would unmount the swap screen and
// kill the hook waiting for the burn tx. Back/reject instead clears the request
// (reject) so the overlay self-unmounts; batch is hidden (a CCTP burn can't be
// deferred). See docs/cctp-mobileweb-onchain-sign-plan.md §3 Phần A.
const SignAccountOpScreen = ({ asOverlay = false }: { asOverlay?: boolean }) => {
  const { currentUserRequest, visibleUserRequests, userRequests } = useRequestsControllerState()
  const liveSignAccountOpState = useSignAccountOpControllerState()
  const accountOpRequest = useMemo(() => {
    if (currentUserRequest?.kind !== 'calls') return undefined
    return currentUserRequest as CallsUserRequest
  }, [currentUserRequest])

  // The background emits every SignAccountOpController instance under the same
  // `SignAccountOpController` name, and the SPA's provider keeps the last state
  // it received for the whole life of the tab (the extension gets a fresh React
  // tree per action window, so it never sees this). Landing on this route again
  // therefore paints the PREVIOUS op until the new controller emits - that is
  // the "Speed up a pending top-up shows the earlier swap" report: the route is
  // entered by `useSpeedUpTransaction` the moment the request is dispatched,
  // seconds before the background finishes building it.
  // Treat the state as belonging to this screen only while it is the controller
  // of the calls request the wallet is currently on. Anything else is stale and
  // must render as "not initialized" (SafetyChecksOverlay) instead of as somebody
  // else's transaction. `hadCallsRequest` keeps the post-broadcast frames alive:
  // once broadcast, `currentUserRequest` flips to the `benzin` request while this
  // screen still needs its final (Done) status to drive the navigation effect below.
  const hadCallsRequestRef = useRef(false)
  const signAccountOpState = useMemo(() => {
    if (!liveSignAccountOpState) return liveSignAccountOpState
    if (accountOpRequest)
      return liveSignAccountOpState.fromRequestId === accountOpRequest.id
        ? liveSignAccountOpState
        : null
    // No calls request on screen: keep the state only if we are watching the op
    // we just signed through to its benzin hand-off.
    // Overlay mode is exempt: it is mounted on top of the swap screen only while
    // its own request is live and unmounts itself when the request clears.
    return hadCallsRequestRef.current || asOverlay ? liveSignAccountOpState : null
  }, [liveSignAccountOpState, accountOpRequest, asOverlay])

  const mainState = useMainControllerState()
  const { account } = useSelectedAccountControllerState()
  const { dispatch } = useBackgroundService()
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { theme } = useTheme()
  const { navigate } = useNavigation()
  const { openReceive } = useReceiveSheet()
  const { mode } = useAppMode()
  const layout = useHomeBreakpoints()
  const { isDesktop } = layout

  const { isLoading: isEnsLoading, ens } = useReverseLookup({ address: account?.addr || '' })

  const [containerHeight, setContainerHeight] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)
  const [hasReachedBottom, setHasReachedBottom] = useState<boolean | null>(null)

  const handleUpdateStatus = useCallback(
    (status: SigningStatus) => {
      dispatch({
        type: 'CURRENT_SIGN_ACCOUNT_OP_UPDATE_STATUS',
        params: { updateType: 'Requests', status }
      })
    },
    [dispatch]
  )
  const updateController = useCallback(
    (params: { signingKeyAddr?: Key['addr']; signingKeyType?: Key['type'] }) => {
      dispatch({
        type: 'CURRENT_SIGN_ACCOUNT_OP_UPDATE',
        params: { updateType: 'Requests', ...params }
      })
    },
    [dispatch]
  )

  const {
    renderedButNotNecessarilyVisibleModal,
    isViewOnly,
    dismissWarning,
    acknowledgeWarning,
    isChooseSignerShown,
    setIsChooseSignerShown,
    onSignButtonClick,
    handleChangeSigningKey,
    warningToPromptBeforeSign,
    handleDismissLedgerConnectModal,
    slowPaymasterRequest,
    slowRequest,
    isSignLoading,
    hasEstimation,
    warningModalRef,
    handleChangeFeePayerKeyType,
    isChooseFeePayerKeyShown,
    setIsChooseFeePayerKeyShown,
    signingKeyType,
    feePayerKeyType,
    shouldDisplayLedgerConnectModal,
    network,
    isSignDisabled,
    bundlerNonceDiscrepancy,
    primaryButtonText,
    shouldHoldToProceed,
    shouldDisplayQrSigningModal,
    handleQrSingingFlowOnContinuePressed,
    handleQrSigningFlowSubmitSignatureResponse,
    handleQrSigningFlowOnClosePressed,
    handleQrSigningFlowOnRejectPressed,
    handleQrSigningFlowOnBackPressed,
    currentRequest,
    signingStep
  } = useSign({
    handleUpdateStatus,
    signAccountOpState,
    handleUpdate: updateController
  })

  // On broadcast success the background `resolveAccountOpRequest` removes this
  // screen's `calls` request and switches `currentUserRequest` to an auto-opened
  // `benzin` (tx-status) request. The extension popup auto-navigates on that
  // change (RequestsControllerStateProvider, gated on `isRequestWindow`); the
  // mobile-web route-based screen has no equivalent driver, so without this it
  // stays mounted with the sign status frozen at `Done` — and `getIsSignLoading`
  // treats `Done` as loading, leaving the button stuck on "Signing…" forever.
  // Mirror the extension popup: once the `calls` request we were signing is gone
  // and no further `calls` request is queued, navigate to the auto-opened
  // `benzin` request — the tx-status screen that polls confirmed/failed, so the
  // user gets explicit success/failure feedback instead of a silent bounce.
  // (`WebMainRoutes` owns the `/benzin` route; its Close button resolves the
  // request and returns to the dashboard.) If the calls request resolved without
  // a benzin (e.g. reject), fall back to the dashboard. Overlay mode is skipped —
  // it self-unmounts when the request clears (a route change would unmount the
  // swap screen + its live CCTP hook).
  useEffect(() => {
    if (asOverlay) return
    if (currentUserRequest?.kind === 'calls') {
      hadCallsRequestRef.current = true
      return
    }
    if (!hadCallsRequestRef.current) return
    hadCallsRequestRef.current = false
    if (currentUserRequest?.kind === 'benzin') {
      navigate(
        WEB_ROUTES.benzin +
          getBenzinUrlParams({
            chainId: currentUserRequest.meta.chainId,
            isInternal: true,
            txnId: currentUserRequest.meta?.txnId,
            identifiedBy: currentUserRequest.meta?.identifiedBy
          })
      )
      return
    }
    navigate(WEB_ROUTES.dashboard)
  }, [currentUserRequest, asOverlay, navigate])

  const dappSession = accountOpRequest?.dappPromises?.[0]?.session
  const callCount = signAccountOpState?.accountOp?.calls?.length || 0
  const chainId = signAccountOpState?.accountOp?.chainId

  // SMART_ACCOUNT_ENABLED=false kill-switch — twin of the guard in the
  // extension SignAccountOpScreen: a request that still targets a Smart Account
  // (stale dApp session, persisted selection) is rejected instead of signed.
  useEffect(() => {
    if (FEATURES.smartAccountEnabled) return
    if (!isSmartAccount(signAccountOpState?.account)) return
    if (!accountOpRequest) return

    dispatch({
      type: 'REQUESTS_CONTROLLER_REJECT_USER_REQUEST',
      params: { err: 'Smart Accounts temporarily disabled', id: accountOpRequest.id }
    })
    if (!asOverlay) navigate(WEB_ROUTES.dashboard)
  }, [accountOpRequest, asOverlay, dispatch, navigate, signAccountOpState?.account])

  const handleRejectAccountOp = useCallback(() => {
    if (!accountOpRequest) return

    dispatch({
      type: 'REQUESTS_CONTROLLER_REJECT_USER_REQUEST',
      params: {
        err: 'User rejected the transaction request.',
        id: accountOpRequest.id,
        options: { shouldOpenNextRequest: visibleUserRequests.length > 1 }
      }
    })
    // In overlay mode the reject clears currentUserRequest → the overlay
    // self-unmounts; navigating would unmount the swap screen + its CCTP hook.
    if (!asOverlay && visibleUserRequests.length <= 1) navigate(WEB_ROUTES.dashboard)
  }, [dispatch, accountOpRequest, visibleUserRequests.length, navigate, asOverlay])

  // Back: route screen → dashboard; overlay → reject (clears request, unmounts
  // overlay) so we never change route out from under the swap screen.
  const onBack = useCallback(
    () => (asOverlay ? handleRejectAccountOp() : navigate(WEB_ROUTES.dashboard)),
    [asOverlay, handleRejectAccountOp, navigate]
  )

  const onNavigate = useCallback(
    (item: NavItem) => {
      if (item.key === 'receive') {
        openReceive()
        return
      }
      if (item.route) navigate(item.route)
    },
    [navigate, openReceive]
  )

  useEffect(() => {
    if (isSignDisabled || !containerHeight || !contentHeight) return
    const isScrollNotVisible = contentHeight <= containerHeight
    if (!hasReachedBottom) setHasReachedBottom(isScrollNotVisible)
  }, [contentHeight, containerHeight, hasReachedBottom, hasEstimation, isSignDisabled])

  const copySignAccountOpError = useCallback(async () => {
    const errorCode = signAccountOpState?.errors?.[0]?.code
    if (!errorCode) return
    await setStringAsync(errorCode)
    addToast(t('Error code copied to clipboard'))
  }, [addToast, signAccountOpState?.errors, t])

  const isUsingFallbackEstimationDueToPendingTx =
    !!signAccountOpState?.estimation.isUsingFallbackEstimationDueToPendingTx

  const errorText = useMemo(() => {
    const { code, text } = signAccountOpState?.errors?.[0] || {}

    if (code) {
      return (
        <AlertVertical.Text type="warning" size="sm">
          {getErrorCodeStringFromReason(code || '', false)}
          <Pressable
            // @ts-ignore web style
            style={{ verticalAlign: 'middle', ...spacings.mlMi, ...spacings.mbMi }}
            onPress={copySignAccountOpError}
          >
            <CopyIcon strokeWidth={1.5} width={20} height={20} color={theme.warningText} />
          </Pressable>
        </AlertVertical.Text>
      )
    }

    if (text) {
      return (
        <AlertVertical.Text type="warning" size="sm">
          {text}
        </AlertVertical.Text>
      )
    }

    return undefined
  }, [copySignAccountOpError, signAccountOpState?.errors, theme.warningText])

  const firstError = signAccountOpState?.errors?.[0]
  const estimationFailed = signAccountOpState?.status?.type === SigningStatus.EstimationError

  // On desktop the inner column owns scrolling and gates Sign on reaching the
  // bottom. On tablet/mobile AppShell owns scrolling (can't nest a flex
  // ScrollView), so we don't gate — the buttons sit at the very bottom anyway.
  const signGateUnmet = isDesktop
    ? typeof hasReachedBottom === 'boolean' && !hasReachedBottom
    : false

  if (mainState.signAccOpInitError) {
    return (
      <AppShell active="send" mode={mode} layout={layout} onNavigate={onNavigate}>
        <View style={[StyleSheet.absoluteFill, flexbox.alignCenter, flexbox.justifyCenter]}>
          <Alert type="error" title={mainState.signAccOpInitError} />
        </View>
      </AppShell>
    )
  }

  // Match the Send screen's card chrome (homeCard tokens, 16px radius, 24px pad).
  const cardStyle = {
    backgroundColor: theme.homeCardBackground as string,
    borderRadius: RADII.lg,
    padding: isDesktop ? 24 : 16
  }
  const header = (
    <View
      style={[
        flexbox.directionRow,
        flexbox.alignCenter,
        flexbox.justifySpaceBetween,
        spacings.mbLg
      ]}
    >
      <View style={[flexbox.directionRow, flexbox.alignCenter]}>
        <Pressable
          onPress={onBack}
          style={({ hovered }: any) => [
            flexbox.center,
            {
              width: 40,
              height: 40,
              borderRadius: RADII.pill,
              backgroundColor: theme.secondaryBackground as string,
              opacity: hovered ? 0.85 : 1,
              ...spacings.mrSm
            }
          ]}
        >
          <LeftArrowIcon color={theme.primaryText} />
        </Pressable>
        <Text fontSize={20} weight="medium">
          {t('Confirm transaction')}
        </Text>
      </View>
      {callCount > 0 ? (
        <View
          style={[
            spacings.phSm,
            spacings.pvMi,
            { borderRadius: RADII.pill, backgroundColor: theme.secondaryBackground as string }
          ]}
        >
          <Text fontSize={12} weight="medium" appearance="tertiaryText">
            {callCount === 1
              ? t('{{count}} CALL', { count: callCount })
              : t('{{count}} CALLS', { count: callCount })}
          </Text>
        </View>
      ) : null}
    </View>
  )

  const accountCard = account ? (
    <View
      style={[
        cardStyle,
        flexbox.directionRow,
        flexbox.alignCenter,
        flexbox.justifySpaceBetween,
        spacings.mbLg
      ]}
    >
      <View style={[flexbox.directionRow, flexbox.alignCenter, flexbox.flex1]}>
        <Avatar
          address={account.addr}
          pfp={account.preferences.pfp}
          isSmart={isSmartAccount(account)}
        />
        <View style={flexbox.flex1}>
          <Text fontSize={16} weight="medium" numberOfLines={1}>
            {account.preferences.label}
          </Text>
          <AccountAddress isLoading={isEnsLoading} ens={ens} address={account.addr} />
        </View>
      </View>
      {dappSession ? (
        <View style={[flexbox.directionRow, flexbox.alignCenter, spacings.plSm]}>
          <View style={{ alignItems: 'flex-end', ...spacings.mrSm }}>
            <Text fontSize={14} weight="medium" numberOfLines={1}>
              {dappSession.name}
            </Text>
            <Text fontSize={12} appearance="secondaryText" numberOfLines={1}>
              {dappSession.url?.replace(/^https?:\/\//, '')}
            </Text>
          </View>
          {dappSession.icon ? (
            <Image
              source={{ uri: dappSession.icon }}
              style={{ width: 40, height: 40, borderRadius: RADII.md }}
              resizeMode="contain"
            />
          ) : null}
        </View>
      ) : null}
    </View>
  ) : null

  const overviewCard = (
    <View style={[cardStyle, spacings.mbLg]}>
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifySpaceBetween,
          spacings.mbSm
        ]}
      >
        <SectionHeading withMb={false}>{t('Overview')}</SectionHeading>
        <NetworkBadge chainId={network?.chainId} withOnPrefix />
      </View>
      <PendingTransactions
        network={network}
        setDelegation={signAccountOpState?.accountOp.meta?.setDelegation}
        delegatedContract={signAccountOpState?.delegatedContract}
      />
      {!!firstError && !isViewOnly ? (
        <AlertVertical type="warning" size="sm" title={firstError.title} text={errorText} />
      ) : (
        <>
          {isUsingFallbackEstimationDueToPendingTx && !isViewOnly ? (
            <AlertVertical
              type="info"
              size="sm"
              title={t('Waiting for previous transaction to confirm')}
              text={
                <AlertVertical.Text type="info" size="sm">
                  {t(
                    "A previous transaction on this account is still pending on-chain. The wallet can't simulate this transaction yet, but you can still sign — it will queue in the mempool and execute right after the previous one is confirmed."
                  )}
                </AlertVertical.Text>
              }
            />
          ) : null}
          <Simulation
            network={network}
            isViewOnly={isViewOnly}
            isEstimationComplete={!!signAccountOpState?.isInitialized && !!network}
          />
        </>
      )}
      {isViewOnly && <NoKeysToSignAlert />}
    </View>
  )

  const feeCard = !estimationFailed ? (
    <View style={[cardStyle, spacings.mbLg]}>
      <Estimation
        signAccountOpState={signAccountOpState}
        disabled={isSignLoading}
        hasEstimation={!!hasEstimation}
        slowRequest={slowRequest}
        isViewOnly={isViewOnly}
        isSponsored={signAccountOpState ? signAccountOpState.isSponsored : false}
        sponsor={signAccountOpState ? signAccountOpState.sponsor : undefined}
        updateType="Requests"
        bundlerNonceDiscrepancy={bundlerNonceDiscrepancy}
      />
    </View>
  ) : null

  // Footer buttons — styled to match the Send screen:
  //   • "Sign"   = blue primary (ButtonWithLoader / HoldToProceed), like "Proceed"
  //   • "Reject" = danger outline, same 48px height
  // "Start a batch" is intentionally removed: batching this signing popup with
  // other pending requests let unrelated batched calls fail together on some
  // dApp flows (e.g. LibertySwap), putting user funds at risk.
  const signDisabled = isSignDisabled || signGateUnmet
  const inProgressButtonText =
    signAccountOpState?.status?.type === SigningStatus.WaitingForPaymaster
      ? t('Sending...')
      : t(
          getSigningProgressText({
            signedTransactionsCount: signAccountOpState?.signedTransactionsCount,
            totalTransactions: signAccountOpState?.accountOp?.calls?.length,
            defaultText: 'Signing...'
          })
        )

  const batchButton = null

  const signButton = shouldHoldToProceed ? (
    <HoldToProceedButton
      text={t('Hold to sign')}
      disabled={signDisabled}
      onHoldComplete={onSignButtonClick}
      style={{ flex: 1, height: 48 }}
      testID="proceed-btn"
    />
  ) : (
    <ButtonWithLoader
      testID="transaction-button-sign"
      type="primary"
      disabled={signDisabled}
      isLoading={isSignLoading}
      spinnerVariant="white"
      text={isSignLoading ? inProgressButtonText : primaryButtonText}
      onPress={onSignButtonClick}
      style={{ flex: 1, height: 48, marginLeft: 0, minWidth: 0 }}
    />
  )

  const rejectButton = (
    <Pressable
      onPress={handleRejectAccountOp}
      disabled={isSignLoading}
      style={({ hovered }: any) => [
        flexbox.center,
        {
          height: 48,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: theme.errorDecorative as string,
          backgroundColor: theme.errorBackground as string,
          opacity: isSignLoading ? 0.5 : hovered ? 0.85 : 1,
          ...spacings.phLg
        }
      ]}
      testID="transaction-button-reject"
    >
      <Text fontSize={16} weight="medium" color={theme.errorDecorative as string}>
        {t('Reject')}
      </Text>
    </Pressable>
  )

  const footer = isDesktop ? (
    batchButton ? (
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifySpaceBetween,
          { gap: 12 }
        ]}
      >
        {rejectButton}
        {/* Right group flexes to fill the row (up to 420) so the two equal buttons
          have room — no extra spacer (a second flex sibling would halve this). */}
        <View
          style={[flexbox.directionRow, flexbox.alignCenter, { gap: 12, flex: 1, maxWidth: 420 }]}
        >
          {batchButton}
          {signButton}
        </View>
      </View>
    ) : (
      // No batch (overlay/swap): Reject + Sign as equal-width halves, same as the
      // mobile row — flexBasis:0 wrappers force exact halves, height:48 caps Sign's
      // flex:1 (which it carries to grow vertically inside the wrapper).
      <View style={[flexbox.directionRow, flexbox.alignCenter, { gap: 12 }]}>
        <View style={{ flex: 1, flexBasis: 0, minWidth: 0, height: 48 }}>{rejectButton}</View>
        <View style={{ flex: 1, flexBasis: 0, minWidth: 0, height: 48 }}>{signButton}</View>
      </View>
    )
  ) : (
    <View style={{ gap: 12 }}>
      <View style={[flexbox.directionRow, flexbox.alignCenter, { gap: 12 }]}>
        {/* Identical flexBasis:0 wrappers force Reject and Sign to exactly equal
          halves; height:48 caps Sign's flex:1 (which grows vertically inside the
          column wrapper) so both stay 48 tall. */}
        <View style={{ flex: 1, flexBasis: 0, minWidth: 0, height: 48 }}>{rejectButton}</View>
        <View style={{ flex: 1, flexBasis: 0, minWidth: 0, height: 48 }}>{signButton}</View>
      </View>
      {/* "Start a batch" moved to the bottom, full width. Fixed 48 height caps
        the button's flex:1 (which it carries for the desktop row split). */}
      {batchButton ? <View style={{ height: 48 }}>{batchButton}</View> : null}
    </View>
  )

  const keySelect = signAccountOpState ? (
    <SigningKeySelect
      isVisible={isChooseSignerShown || isChooseFeePayerKeyShown}
      isSigning={isSignLoading || !signAccountOpState.readyToSign}
      handleClose={() => {
        setIsChooseSignerShown(false)
        setIsChooseFeePayerKeyShown(false)
      }}
      selectedAccountKeyStoreKeys={
        isChooseFeePayerKeyShown
          ? signAccountOpState.feePayerKeyStoreKeys
          : signAccountOpState.accountKeyStoreKeys
      }
      handleChooseKey={
        isChooseFeePayerKeyShown ? handleChangeFeePayerKeyType : handleChangeSigningKey
      }
      type={isChooseFeePayerKeyShown ? 'broadcasting' : 'signing'}
      account={signAccountOpState.account}
    />
  ) : null

  const column = (
    <>
      {header}
      {accountCard}
      {overviewCard}
      {feeCard}
      {footer}
    </>
  )

  return (
    <AppShell active="send" mode={mode} layout={layout} onNavigate={onNavigate}>
      <SafetyChecksOverlay
        shouldBeVisible={
          !signAccountOpState?.isInitialized || !!signAccountOpState.safetyChecksLoading
        }
      />
      <Modals
        renderedButNotNecessarilyVisibleModal={renderedButNotNecessarilyVisibleModal}
        signAccountOpState={signAccountOpState}
        warningModalRef={warningModalRef}
        feePayerKeyType={feePayerKeyType}
        signingKeyType={signingKeyType}
        slowPaymasterRequest={slowPaymasterRequest}
        shouldDisplayLedgerConnectModal={shouldDisplayLedgerConnectModal}
        handleDismissLedgerConnectModal={handleDismissLedgerConnectModal}
        warningToPromptBeforeSign={warningToPromptBeforeSign}
        acknowledgeWarning={acknowledgeWarning}
        dismissWarning={dismissWarning}
        shouldDisplayQrSigningModal={shouldDisplayQrSigningModal}
        handleQrSingingFlowOnContinuePressed={handleQrSingingFlowOnContinuePressed}
        handleQrSigningFlowSubmitSignatureResponse={handleQrSigningFlowSubmitSignatureResponse}
        handleQrSigningFlowOnClosePressed={handleQrSigningFlowOnClosePressed}
        handleQrSigningFlowOnRejectPressed={handleQrSigningFlowOnRejectPressed}
        handleQrSigningFlowOnBackPressed={handleQrSigningFlowOnBackPressed}
        currentRequest={currentRequest}
        signingStep={signingStep}
      />
      {keySelect}
      {isDesktop ? (
        <View style={{ flex: 1, alignItems: 'center' }}>
          <ScrollView
            style={{ flex: 1, width: '100%' }}
            contentContainerStyle={{
              width: '100%',
              maxWidth: CONTENT_MAX_WIDTH,
              alignSelf: 'center',
              paddingVertical: 40,
              paddingHorizontal: 24
            }}
            showsVerticalScrollIndicator={false}
            onScroll={(e) => {
              if (isCloseToBottom(e.nativeEvent)) setHasReachedBottom(true)
            }}
            onLayout={(e) => setContainerHeight(e.nativeEvent.layout.height)}
            onContentSizeChange={(_, height) => setContentHeight(height)}
            scrollEventThrottle={400}
          >
            {column}
          </ScrollView>
        </View>
      ) : (
        // Tablet/mobile: AppShell already provides the scrolling container.
        <View style={{ width: '100%' }}>{column}</View>
      )}
    </AppShell>
  )
}

export default React.memo(SignAccountOpScreen)
